import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppAuth } from "@/auth/use-app-auth";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { clearInvitationToken, persistAuthReturnTo, persistInvitationToken } from "@/lib/auth-flow";

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { loginWithRedirect } = useAppAuth();
  const [invitation, setInvitation] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError("Invitation token is missing.");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`/api/auth/invitations/validate?token=${token}`);
        setInvitation(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Unable to validate invitation.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleContinue = async (connection: "email" | "google-oauth2") => {
    if (!invitation) return;
    setSubmitting(true);
    setError(null);
    try {
      setSuccess(true);
      persistAuthReturnTo("/org-setup/onboarding");
      persistInvitationToken(token);
      await loginWithRedirect({
        appState: { returnTo: "/org-setup/onboarding" },
        authorizationParams: {
          connection,
          login_hint: invitation.email,
        },
      });
    } catch (err: any) {
      // The redirect never happened — drop the stashed token so a later,
      // unrelated login doesn't silently associate this user with the org.
      clearInvitationToken();
      setSuccess(false);
      setError(err?.response?.data?.message || "Unable to accept invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold">
            Invitation
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">Join your team on theRecruit</h1>
          <p className="text-sm text-slate-600 mt-2">
            Sign in with the verified email that received this invitation to join the organization.
          </p>
        </div>

        {loading && <p className="text-sm text-slate-600">Loading invitation...</p>}

        {!loading && error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && invitation && (
          <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">{invitation.organization?.name}</p>
            <p className="text-slate-600">
              Invited by {invitation.invitedBy?.name} • Role: {invitation.role}
            </p>
          </div>
        )}

        {!loading && invitation && !success && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <Input value={invitation.email} disabled />
            </div>
            <Button
              onClick={() => handleContinue("email")}
              className="w-full bg-amber-500 text-amber-900 hover:bg-amber-400"
              disabled={submitting}
            >
              {submitting ? "Redirecting..." : "Continue with Email Link"}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleContinue("google-oauth2")}
              className="w-full"
              disabled={submitting}
            >
              Continue with Google
            </Button>
          </div>
        )}

        {success && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Redirecting you to sign in...
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation;
